import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Eye } from 'lucide-react';
import { crushshyAdminApi } from '../../../services/api';
import { useToast } from '../../../hooks/use-toast';
import { useCrushShyT, crushshyLocale } from '../../../hooks/useCrushShyT';
import { useLanguage } from '../../../context/LanguageContext';
import CrushShyPageHeader from './CrushShyPageHeader';
import { crushCard, crushBtnPrimary } from './crushshyTheme';

const CrushShyReports = () => {
  const [reports, setReports] = useState([]);
  const [filter, setFilter] = useState('');
  const { toast } = useToast();
  const t = useCrushShyT();
  const { language } = useLanguage();

  const reasonLabels = useMemo(
    () => ({
      harassment: t('reasonHarassment'),
      inappropriate: t('reasonInappropriate'),
      spam: t('reasonSpam'),
      fake_profile: t('reasonFake'),
    }),
    [t]
  );

  const statusLabels = useMemo(
    () => ({
      open: { label: t('reportStatusOpen'), className: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
      reviewed: {
        label: t('reportStatusReviewed'),
        className: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
      },
      dismissed: {
        label: t('reportStatusDismissed'),
        className: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
      },
    }),
    [t]
  );

  const filterOptions = useMemo(
    () => [
      { value: '', label: t('reportsFilterAll') },
      { value: 'open', label: t('reportsFilterOpen') },
      { value: 'reviewed', label: t('reportsFilterReviewed') },
      { value: 'dismissed', label: t('reportsFilterDismissed') },
    ],
    [t]
  );

  const load = async () => {
    try {
      const res = await crushshyAdminApi.getReports(filter ? { status: filter } : {});
      setReports(res.data.reports || []);
    } catch {
      toast({ title: t('toastLoadReportsError'), variant: 'destructive' });
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const setStatus = async (id, status) => {
    try {
      await crushshyAdminApi.updateReport(id, { status });
      toast({ title: t('toastReportUpdated') });
      load();
    } catch {
      toast({ title: t('toastError'), variant: 'destructive' });
    }
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString(crushshyLocale(language));
    } catch {
      return iso;
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-5xl">
      <CrushShyPageHeader title={t('reportsTitle')} subtitle={t('reportsSubtitle')} />

      <div className="flex gap-2 mb-6 flex-wrap">
        {filterOptions.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setFilter(f.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
              filter === f.value
                ? 'bg-pink-500/15 border-pink-500/40 text-pink-300'
                : 'border-white/10 text-gray-400 hover:border-pink-500/20'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {reports.map((r, i) => {
          const st = statusLabels[r.status] || statusLabels.open;
          return (
            <motion.article
              key={r.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className={`${crushCard} p-5`}
            >
              <div className="flex flex-wrap justify-between gap-3 mb-3">
                <div>
                  <p className="text-white font-semibold">
                    {r.reportedUserName}{' '}
                    <span className="text-gray-500 font-normal">{t('reportsReportedBy')}</span> {r.reporterUserName}
                  </p>
                  <p className="text-sm text-pink-400/90 mt-0.5">
                    {reasonLabels[r.reason] || r.reason} · {formatDate(r.createdAt)}
                  </p>
                </div>
                <span className={`text-xs px-3 py-1 rounded-full border h-fit ${st.className}`}>{st.label}</span>
              </div>
              <p className="text-gray-400 text-sm mb-4">{r.details}</p>
              <div className="flex flex-wrap gap-2">
                {r.status === 'open' && (
                  <>
                    <button
                      type="button"
                      onClick={() => setStatus(r.id, 'reviewed')}
                      className={`${crushBtnPrimary} flex items-center gap-2 text-sm py-2`}
                    >
                      <CheckCircle className="w-4 h-4" />
                      {t('reportsMarkReviewed')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setStatus(r.id, 'dismissed')}
                      className="px-4 py-2 rounded-lg border border-white/15 text-gray-300 hover:bg-white/5 flex items-center gap-2 text-sm"
                    >
                      <XCircle className="w-4 h-4" />
                      {t('reportsDismiss')}
                    </button>
                  </>
                )}
                <a
                  href="/admin/crushshy/users"
                  className="px-4 py-2 rounded-lg border border-purple-500/30 text-purple-300 hover:bg-purple-500/10 flex items-center gap-2 text-sm"
                >
                  <Eye className="w-4 h-4" />
                  {t('reportsViewUsers')}
                </a>
              </div>
            </motion.article>
          );
        })}
        {reports.length === 0 && <p className="text-center text-gray-500 py-16">{t('reportsEmpty')}</p>}
      </div>
    </div>
  );
};

export default CrushShyReports;
